import { z } from 'zod';
import { OfferIdSchema, PlayerIdSchema } from '../ids';
import { TimestampMsSchema } from '../time';
import { ObjectViewSchema } from './inventory';
import { ItemAmountsSchema, ItemIdSchema, PositiveQuantitySchema } from './items';

/** Currency amounts: whole coins. */
export const CoinsSchema = z.number().int().nonnegative().max(1_000_000_000);

/** A unit price in coins. Bounds are game config; this only guards the wire. */
export const UnitPriceSchema = z.number().int().positive().max(1_000_000);

/** `spoiled`: everything it sold went bad while listed (ITEM-PERISH, PD-6), and it closed itself. */
export const OfferStatusSchema = z.enum(['open', 'sold', 'cancelled', 'spoiled']);
export type OfferStatus = z.infer<typeof OfferStatusSchema>;

const PartySchema = z.object({ playerId: PlayerIdSchema, displayName: z.string() });

/**
 * An offer of the auction (ECON-MARKET step 2, MK-6): a whole lot of one item at a unit price,
 * bought whole from anywhere. Its goods wait at the auction house where it was listed, in its
 * escrow while it is open, and are taken out there.
 */
export const OfferSchema = z.object({
  id: OfferIdSchema,
  /** The auction house where its goods wait (the layout's `auctionHouses`). */
  house: z.string(),
  seller: PartySchema,
  item: ItemIdSchema,
  quantity: PositiveQuantitySchema,
  unitPrice: UnitPriceSchema,
  status: OfferStatusSchema,
  buyer: PartySchema.nullable(),
  createdAt: TimestampMsSchema,
  closedAt: TimestampMsSchema.nullable(),
  /**
   * When the first of its perishable goods goes bad (ITEM-PERISH, PD-6, server game time); listed
   * open offers only. `quantity` is what is still good.
   */
  expiresAt: TimestampMsSchema.optional(),
});
export type Offer = z.infer<typeof OfferSchema>;

/**
 * The NPC merchant's prices per item it trades (in definition order): what it charges and what
 * it pays, per unit. Items it does not trade are absent.
 */
export const MerchantPricesSchema = z.record(
  ItemIdSchema,
  z.object({
    sells: UnitPriceSchema,
    buys: UnitPriceSchema,
    /** What the town's shop holds of it (ECON-MARKET): it sells no more, and its price follows. */
    stock: z.number().int().nonnegative().optional(),
  }),
);
export type MerchantPrices = z.infer<typeof MerchantPricesSchema>;

/**
 * What a character keeps at an auction house (MK-8): what it bought there and what it listed and
 * took back, until it takes it out standing by the house's door.
 */
export const VaultViewSchema = z.object({
  house: z.string(),
  items: ItemAmountsSchema,
  objects: z.array(ObjectViewSchema),
});
export type VaultView = z.infer<typeof VaultViewSchema>;
